import authFetch from "../../authFetch";

/*
 * haProxy.js — client helper for /api/ha-proxy.
 *
 * All Smart Home REST calls go through Command's backend, so users
 * never have to configure http.cors_allowed_origins in HA. The decrypted
 * token is only handled server-side; the browser never sees it for
 * saved-mode requests.
 *
 * WebSocket subscriptions (useHomeAssistant) stay direct from browser →
 * HA because WS isn't subject to CORS in the same way and direct
 * connections preserve sub-second state-change latency.
 *
 * Two request modes:
 *   Saved:  { locId, method?, path, body? } — uses stored creds
 *   Inline: { haUrl, token, method?, path, body? } — setup test only
 */

/** Low-level: returns the fetch Response so callers can stream/blob. */
export async function haProxyRequest({ locId, haUrl, token, method = "GET", path, body }) {
  const payload = { method, path };
  if (locId) payload.locId = locId;
  if (haUrl) payload.haUrl = haUrl;
  if (token) payload.token = token;
  if (body !== undefined) payload.body = body;

  return authFetch("/api/ha-proxy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/** Parse response as JSON. Throws with server-provided error on !ok. */
export async function haProxyJson(args) {
  const res = await haProxyRequest(args);
  const text = await res.text();
  if (!res.ok) {
    let msg;
    try { msg = JSON.parse(text).error; } catch { msg = text || `HTTP ${res.status}`; }
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
}

/** Fetch a binary resource (camera snapshot) as a Blob. */
export async function haProxyBlob({ locId, path }) {
  const res = await haProxyRequest({ locId, method: "GET", path });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let msg;
    try { msg = JSON.parse(text).error; } catch { msg = text || `HTTP ${res.status}`; }
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return res.blob();
}
